import * as Sentry from "@sentry/nextjs";
import { bookingRefundOffers, bookingRefundOffersPatch, bookingReleaseOffers, bookingReleaseOffersPatch } from "../../../app/api/cancelBooking";
import { nolOffersForLegs } from "../../../Lib/Utils/nolCard";
import { cancelLegsForDirection } from "../../../Components/OnwardTravel/bookedNolForLeg";
import { nolPassesBlockedByTicket } from "../../LogicActions/CancelBooking/nolPassesBlockedByTicket";
import { ticketFulfillmentIdsForLeg } from "../../../Components/OnwardTravel/nolTicketRefund";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBooking, ISelectedPassengerForCancelIds, } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IBookingRefundOffersResponse, IBookingReleaseOffersResponse, IRefundOffer, IRefundOfferRequest, IReleaseOffer } from "../../../Orchestrator/Types/Types/BookingTypes";
import { fulfillmentIdSet, isRefundableFulfillment } from "../../../Lib/Utils/refundableFulfillments";
import { showNotification } from "../../../Services/notificationService";
import { getBookingAction } from "../../LogicActions/Booking/GetBookingAction";


/**
 * What one direction's release+refund actually did, so `run()` can stop
 * reporting success for a cancellation the backend rejected.
 */
interface IRefundOutcome {
    /** Ids were sent to /release-offers. False = nothing was refundable. */
    sent: boolean;
    /** A release/refund POST or PATCH came back non-200. */
    failed: boolean;
    /**
     * WHY it failed, in enough detail to diagnose from a log or a Sentry event.
     *
     * ACPT-018: every failure used to reduce to one generic toast and a bare
     * `console.error("release/refund rejected")`. The acceptance sheet for the
     * round-trip failure records that logs were never captured — there was
     * nothing to capture. The step, the HTTP code and the retailer's own error
     * body all land here now.
     */
    reason?: string;
    /** Whatever the API returned alongside the non-200, for Sentry. */
    detail?: unknown;
    /** Nol passes this direction kept out of the refund — see releaseWithoutRefund. */
    blockedNol?: string[];
}

export class ConfirmCancelClickAction extends BaseAction<IDefaultActionResult, {
    selectedBookingIdForCancel: string,
    booking: IBooking,
    selectedPassengerForCancel: ISelectedPassengerForCancelIds[],
    purchaserEmail: string,
    selectedPassengerForCancelInBound: ISelectedPassengerForCancelIds[],
    isFrom: string
}> {
    override async run(): Promise<IDefaultActionResult> {
        let result: IDefaultActionResult = {
            data: null,
            message: "",
            status: "fail"
        }

        // Guards the catch below against refreshing a second time. The first
        // refresh has already replaced the selection atoms, so re-running it
        // would intersect the widened state with itself and undo the narrowing.
        let refreshed = false;

        try {
            const { selectedBookingIdForCancel, booking, selectedPassengerForCancel, purchaserEmail, selectedPassengerForCancelInBound, isFrom } = this.params!;

            const outcomes: IRefundOutcome[] = [];

            /**
             * ACPT-018 — the round-trip failure.
             *
             * The two directions are separate release+refund calls, deliberately:
             * "the retailer rejects a single refund-offers request whose
             * fulfillmentIds span two legs" (er-transcity,
             * `apps/@sds/beef/handlers/payment_structs.go`, `RefundLeg`).
             *
             * But a NOL day pass covers the legs of BOTH directions, and
             * `nolOffersForLegs` matches it for each — so on a round trip the
             * SAME pass fulfillment id went into the outbound body AND the
             * inbound body. The first call released and refunded it; the second
             * re-sent a fulfillment the retailer had just retired, which rejects
             * the whole batch. The inbound leg was therefore never cancelled and
             * the passenger got a generic error. Reproduced in
             * `tests/Actions/cancelRoundTripSharedNolPass.test.ts`: release
             * bodies were `[adm-out, adm-nol]` then `[adm-in, adm-nol]`.
             *
             * The offer-level dedup inside `refundSelectedPassenger` could not
             * see it — it is scoped to one call. This set spans both, so the
             * pass is refunded exactly once, by whichever direction claims it
             * first. That matches the QUOTE, which dedups the same way.
             */
            const alreadySent = new Set<string>();

            // OutBound Cancel Passenger Or Trip
            if (selectedPassengerForCancel.length > 0) {
                outcomes.push(await this.refundSelectedPassenger(selectedBookingIdForCancel, booking, selectedPassengerForCancel, TrainTypeEnum.outbound, purchaserEmail, alreadySent))
            }
            // Inbound Cancel Passenger Or Trip
            if (selectedPassengerForCancelInBound.length > 0) {
                outcomes.push(await this.refundSelectedPassenger(selectedBookingIdForCancel, booking, selectedPassengerForCancelInBound, TrainTypeEnum.inbound, purchaserEmail, alreadySent))
            }

            /**
             * A rejected release/refund used to leave `status = "success"`: every
             * non-200 branch simply fell through, the confirmation toast fired,
             * and CancelBookingInitStep routed to a details page rendering a
             * cancelled leg — for a booking still live on the backend.
             *
             * `sent` distinguishes the two ways nothing happens. One leg of a
             * round trip contributing no ids is normal (its passengers were
             * already refunded, and the other leg still cancels), so it is not
             * a failure on its own — but NO leg sending anything means the
             * passenger pressed Cancel and the app called nothing at all.
             */
            /**
             * A pass whose ticket refunds nothing is CANCELLED with it, for
             * nothing: released, never refunded — refund-offers would pay the
             * pass out on its own rules (AED 40 on a Saver fare). Turnit does not
             * cascade the ticket's cancellation to the pass, so left alone it
             * stays FULFILLED: the booking reads "Confirmed" and the Nol modal
             * offers a Refund again. Verified on INT booking TGWLSW, 2026-09-23:
             * a release-only pass lands RELEASED with nothing paid back.
             *
             * AFTER both directions, and only for passes neither refunded: a
             * round-trip pass blocked by a Saver outbound may still be refunded
             * by a Flex inbound. Skipped once anything failed — a ticket that did
             * not cancel must not lose its pass.
             */
            // ...and only where its ticket is cancelled: a BLOCKED pass that also
            // covers a leg the passenger keeps is still needed there. Web sells one
            // pass per leg, so this is defensive; the refund path is unchanged.
            const cancelledLegIds = new Set<string>([
                ...(selectedPassengerForCancel.length > 0 ? cancelLegsForDirection(booking as any, TripTypeEnum.OUT_BOUND) : []),
                ...(selectedPassengerForCancelInBound.length > 0 ? cancelLegsForDirection(booking as any, TripTypeEnum.IN_BOUND) : []),
            ]);
            // Admission-shaped passes only: that is the shape verified to release
            // cleanly on INT. An ancillary pass has no release path (it refunds
            // straight through refund-offers), and the commit never sends one anyway.
            //
            // And only once every rider's ticket on those legs was really SENT: the
            // commit skips some tickets the gate can see (an offer holding an
            // EXCHANGED admission, see getAllOfferWithoutExchange), and a ticket
            // the passenger keeps must keep its pass.
            const bookingResponse = { booking } as any;
            const passAdmission = (id: string): any =>
                (booking.bookedOffers || []).flatMap((offer: any) => offer?.admissions || [])
                    .find((admission: any) => admission?.id === id);
            const releaseOnly = [...new Set(outcomes.flatMap(outcome => outcome.blockedNol || []))]
                .filter(id => !alreadySent.has(id))
                .filter(id => {
                    const pass = passAdmission(id);
                    // The refund path's own predicate: FULFILLED and issued.
                    if (!pass || !isRefundableFulfillment(pass, fulfillmentIdSet(booking))) return false;
                    const legs: string[] = pass?.tripCoverage?.coveredLegIds || [];
                    if (legs.length === 0 || !legs.every(legId => cancelledLegIds.has(legId))) return false;
                    return legs.every(legId => (pass?.passengerIds || []).every((riderId: string) => {
                        const tickets = ticketFulfillmentIdsForLeg(bookingResponse, legId, riderId);
                        return tickets.length > 0 && tickets.every(ticketId => alreadySent.has(ticketId));
                    }));
                });
            const anyFailedBefore = outcomes.some(outcome => outcome.failed);
            if (releaseOnly.length > 0 && !anyFailedBefore) {
                outcomes.push(await this.releaseWithoutRefund(selectedBookingIdForCancel, releaseOnly));
            }
            // Skipped because a ticket failed: the pass is still live beside a ticket
            // that may be half-cancelled — same support follow-up as a failed release.
            const nolLeftLive = (releaseOnly.length > 0 && anyFailedBefore)
                || outcomes.some(outcome => outcome.failed && outcome.reason?.includes("(no refund)"));

            const anySent = outcomes.some(outcome => outcome.sent);
            const anyFailed = outcomes.some(outcome => outcome.failed);

            if (anyFailed || !anySent) {
                /**
                 * The REAL error, not "release/refund rejected".
                 *
                 * ACPT-018 could not be diagnosed because this branch discarded
                 * everything it knew: which call failed, what the retailer said,
                 * and which fulfillment ids were in the body. All of it is on
                 * the outcome now, and it goes to Sentry as well as the console
                 * so a failure in the field leaves a trace.
                 */
                const reasons = outcomes
                    .filter(outcome => outcome.reason)
                    .map(outcome => outcome.reason)
                    .join("; ")
                    || (anyFailed ? "release/refund rejected" : "no refundable fulfillments to send");
                console.error("Cancel booking did not complete:", reasons, outcomes);
                Sentry.captureMessage(`cancel booking failed: ${reasons}`, {
                    level: "error",
                    tags: {
                        action: "ConfirmCancelClick",
                        // A pass left FULFILLED beside its cancelled ticket: the Nol modal
                        // would refund it on a retry. Support must release it by hand.
                        ...(nolLeftLive ? { nolReleaseOnlyFailed: "true" } : {}),
                    },
                    extra: {
                        bookingId: selectedBookingIdForCancel,
                        outbound: selectedPassengerForCancel?.length || 0,
                        inbound: selectedPassengerForCancelInBound?.length || 0,
                        outcomes,
                    },
                });
                /**
                 * ALWAYS toast. `apiRequest` cannot be relied on to have done it:
                 * `ExcludedAPIsNotification` holds `{endpoint:"bookings",
                 * method:"PATCH"}` and `isNotificationExcluded` matches with
                 * `.includes`, so BOTH release-offers and refund-offers PATCH URLs
                 * are suppressed there. A failed PATCH is precisely the
                 * released-but-not-refunded case — the passenger loses the ticket
                 * AND the money — and it would otherwise close the modal, block
                 * the route, and show nothing at all. `!anySent` raises no toast
                 * either, having called nothing.
                 *
                 * A duplicate toast on the two POST paths (same "api-error"
                 * className) is far cheaper than silence on a money-loss path.
                 */
                showNotification(
                    "api_error",
                    getTranslatedMessage("apiErrorMsg"),
                    "",
                    3,
                    "api-error"
                );
                // Only when something was actually sent. `!anySent` changed
                // nothing server-side, so there is no stale snapshot to replace —
                // and refreshing there would reset the passenger's selection for
                // no reason.
                if (anyFailed) { refreshed = true; await this.refreshAfterFailure(); }
                return result;
            }

            if (isFrom === "cancel-shuttle") {
                getBookingAction.setParams({ bookingId: selectedBookingIdForCancel });
                await getBookingAction.execute();
                // Resting the Recoil state
                updateRecoilState("selectedPassengerForCancelAtom", []);
                updateRecoilState("selectedPassengerForCancelInBoundAtom", []);
            }

            showNotification(
                "info",
                getTranslatedMessage("cancelConfirmTitle"),
                getTranslatedMessage("cancelConfirmMessage"),
                3
            );

            result.status = "success"

        } catch (error) {
            result.status = "fail"
            /**
             * A THROW is the other way ACPT-018 shows up as a bare
             * "Something went wrong" — `passengerAdmission.passengerIds` was
             * dereferenced without a guard and the field is `nullable` on the
             * wire (docs/retailer.json, BookingSnapshotAdmission), so one
             * unattributed admission on the booking crashed the whole
             * cancellation into here. That is now guarded at the read, and
             * anything else that reaches this catch is reported rather than
             * reduced to a console line the field never sees.
             */
            console.error("Error in ConfirmCancelClick action:", error);
            Sentry.captureException(error, {
                tags: { action: "ConfirmCancelClick" },
                extra: { bookingId: this.params?.selectedBookingIdForCancel },
            });
            showNotification("api_error", getTranslatedMessage("apiErrorMsg"), "", 3, "api-error");
            // Same refresh as the checked-failure branch. refundSelectedPassenger
            // can throw, and that path lands here — leaving the stale snapshot
            // this exists to replace. Skipped when the branch above already ran
            // it, or a throw from inside the refresh itself would re-enter and
            // intersect the widened selection with itself.
            if (!refreshed) await this.refreshAfterFailure();
        }
        finally {
            return result;
        }
    }

    /**
     * Re-read the booking after a failed cancel, so the retry works from live
     * data instead of this snapshot. After a PARTIAL failure (outbound refunded,
     * inbound rejected) the snapshot still calls the outbound admissions
     * FULFILLED, so a retry re-sends ids the backend has just marked REFUNDED —
     * the dead-id batch isRefundableFulfillment exists to keep out — and every
     * retry is rejected too, permanently, until a page reload.
     *
     * MIRRORS CancelBookingRedirectClickAction's `getBookingParams`. Refetching
     * with `{bookingId}` alone is NOT equivalent: GetBookingAction then re-derives
     * from the whole booking and rewrites selectedBookingTripStatusAtom,
     * selectedPassengerForCancelAtom and selectedPassengerForCancelInBoundAtom
     * with every FULFILLED passenger on both legs. CancelTripRefundTicket builds
     * the retry payload straight off those atoms with inboundCheckboxChecked
     * defaulting to true, so a scoped entry (the CDT split-card shape carrying
     * `tripIds`/`passengerData`) would silently re-scope to the FULL booking and
     * the next click would cancel legs and passengers the user never selected.
     * Widening a cancellation is worse than the stale ids this fixes.
     *
     * `selectedTripsDataAtom` is written on entry by CancelBookingRedirectClick
     * and nothing on this page clears it.
     */
    private refreshAfterFailure = async () => {
        try {
            await this.refreshAfterFailureUnsafe();
        } catch (error) {
            // Never throw into the caller: run()'s catch is one of them, and a
            // throw from here would both mask the original failure and re-enter
            // this refresh. A stale retry is worse than a lost refresh, so log it.
            console.error("Cancel booking: refresh after failure threw; retry will use stale data", error);
        }
    }

    private refreshAfterFailureUnsafe = async () => {
        const bookingId = this.params?.selectedBookingIdForCancel;
        if (!bookingId) return;
        const selectedTripsData = getRecoilStateValue<any>("selectedTripsDataAtom");

        // The user's per-passenger checkboxes live in these two atoms
        // (PassengerOutboundCheckAction / MainOutBoundCheckAction write them),
        // and GetBookingAction rewrites both unconditionally with EVERY FULFILLED
        // passenger (:269, :286). Mirroring the trip params is not enough to stop
        // that — the trip dimension and the passenger dimension widen separately.
        const chosenOutbound = getRecoilStateValue<string[]>("selectedPassengerForCancelAtom") || [];
        const chosenInbound = getRecoilStateValue<string[]>("selectedPassengerForCancelInBoundAtom") || [];

        getBookingAction.setParams({
            bookingId,
            direction: selectedTripsData?.direction,
            displayPassengerData: selectedTripsData?.passengers,
            selectedTripsData
        });
        await getBookingAction.execute();

        // Re-narrow to what the user actually picked, INTERSECTED with what is
        // still FULFILLED — so ids the backend has just refunded drop out while
        // passengers they deliberately unchecked do not come back. Without this,
        // unchecking 2 of 3 passengers and hitting a rejected release would
        // silently re-tick all 3, and the next Confirm cancels all 3.
        const asIds = (value: any): string[] =>
            (value || []).map((entry: any) => (typeof entry === "string" ? entry : entry?.passengerId));
        const freshOutbound = asIds(getRecoilStateValue<any[]>("selectedPassengerForCancelAtom"));
        const freshInbound = asIds(getRecoilStateValue<any[]>("selectedPassengerForCancelInBoundAtom"));
        updateRecoilState(
            "selectedPassengerForCancelAtom",
            freshOutbound.filter(id => asIds(chosenOutbound).includes(id))
        );
        updateRecoilState(
            "selectedPassengerForCancelInBoundAtom",
            freshInbound.filter(id => asIds(chosenInbound).includes(id))
        );

        // A failed refresh leaves the stale snapshot in place, so the retry is
        // still doomed — say so rather than letting it look recovered.
        if (!isActionRunSuccessfully(getBookingAction)) {
            console.error("Cancel booking: refresh after failure did not complete; retry will use stale data");
        }
    }

    getAllOfferWithoutExchange(bookedOffers: IBookedOffer[]) {
        const allBookedOfferWithoutExchange: IBookedOffer[] = []
        bookedOffers?.forEach((offer: IBookedOffer) => {
            const offerAdmissions = offer.admissions;
            const isAnyFulfilled = offerAdmissions.find(d => d.status === "EXCHANGED" || d.status === "EXCHANGE_ONGOING")
            if (!isAnyFulfilled) {
                allBookedOfferWithoutExchange.push(offer)
            }
        })
        return allBookedOfferWithoutExchange
    }

    /**
     * One line naming the step, the HTTP code and whatever the retailer said.
     *
     * The api layer puts the body on `error`/`message` depending on the shape;
     * both are stringified rather than picked, because a diagnosis that drops
     * the one field the retailer actually used is how ACPT-018 stayed unlogged.
     */
    private describeFailure = (step: string, response: any, fulfillmentIds: string[]): string => {
        const code = response?.code ?? response?.status ?? "no response";
        const body = response?.error ?? response?.message ?? response?.answer;
        const detail = body === undefined ? "" : ` — ${JSON.stringify(body)?.slice(0, 500)}`;
        return `${step} returned ${code}${detail} (fulfillmentIds: ${fulfillmentIds.join(",")})`;
    }

    /** Release (cancel) these fulfillments and stop — no refund-offers. */
    private releaseWithoutRefund = async (bookingId: string, fulfillmentIds: string[]): Promise<IRefundOutcome> => {
        const outcome: IRefundOutcome = { sent: true, failed: false };
        const response: any = await bookingReleaseOffers({ fulfillmentIds }, bookingId);
        const releaseIds = (response?.answer?.releaseOffers || []).map((d: IReleaseOffer) => d.id);
        if (response?.code !== 200 || releaseIds.length === 0) {
            outcome.failed = true;
            outcome.reason = this.describeFailure("release-offers (no refund)", response, fulfillmentIds);
            outcome.detail = response;
            return outcome;
        }
        for (const releaseId of releaseIds) {
            const patch: any = await bookingReleaseOffersPatch(releaseId, bookingId);
            if (patch?.code !== 200) {
                outcome.failed = true;
                outcome.reason = this.describeFailure(`release-offers PATCH ${releaseId} (no refund)`, patch, fulfillmentIds);
                outcome.detail = patch;
            }
        }
        return outcome;
    }

    refundSelectedPassenger = async (
        bookingId: string,
        booking: IBooking,
        selectedPassengerForCancel: ISelectedPassengerForCancelIds[],
        tripStatus: string,
        purchaserEmail: string,
        /**
         * Fulfillment ids already sent by an EARLIER direction of this same
         * cancellation, mutated as this one claims more. See the ACPT-018 note
         * in `run()` — a Nol pass covering both directions must be refunded
         * once, not once per direction.
         */
        alreadySent: Set<string> = new Set()): Promise<IRefundOutcome> => {
        const outcome: IRefundOutcome = { sent: false, failed: false };
        const allBookedOfferWithoutExchange: IBookedOffer[] = this.getAllOfferWithoutExchange(booking.bookedOffers);

        const tripDirection = tripStatus === TrainTypeEnum.outbound ? TripTypeEnum.OUT_BOUND : TripTypeEnum.IN_BOUND;

        const matchingTrips = booking.trips?.filter((trip: any) => trip.direction === tripDirection) || [];
        const matchingTripIds = new Set(matchingTrips.map((trip: any) => trip.id));

        const matchingOffers: IBookedOffer[] = allBookedOfferWithoutExchange.filter((offer: IBookedOffer) => {
            const coveredTripId = offer.tripCoverage?.coveredTripId;
            return coveredTripId && matchingTripIds.has(coveredTripId);
        });

        // A NOL pass has NO offer-level tripCoverage — only its ADMISSION names
        // the legs — so the filter above drops it and the cancellation refunded
        // the train while leaving the pass unrefunded. MUST match the selection
        // CancelBookingInitAction makes for the quote: the two paths disagreeing
        // is what shipped the earlier shuttle bug (see isRefundableFulfillment).
        // Widened past the trips still on the booking — see `cancellableLegIds`.
        const cancelledLegIds = cancelLegsForDirection(booking as any, tripDirection);
        const selectedOfferIds = new Set(matchingOffers.map((o) => o.offerId));
        for (const nolOffer of nolOffersForLegs(booking, cancelledLegIds)) {
            if (!selectedOfferIds.has(nolOffer.offerId)) matchingOffers.push(nolOffer);
        }

        if (matchingOffers.length > 0) {
            const fulfillmentIds = fulfillmentIdSet(booking);
            const passengerFulfillmentIds: string[] = []
            // A pass follows its ticket — same exclusion the quote makes.
            const blockedNol = await nolPassesBlockedByTicket(
                { booking } as any,
                cancelledLegIds,
                selectedPassengerForCancel.map((p: any) => (typeof p === "string" ? p : p?.passengerId))
            );
            outcome.blockedNol = [...blockedNol];

            selectedPassengerForCancel.forEach((passengerId: any) => {
                matchingOffers.forEach((offer) => {
                    const admissions = offer.admissions || [];
                    // `passengerIds` is NULLABLE on an admission
                    // (docs/retailer.json, BookingSnapshotAdmission). Read bare,
                    // one unattributed admission threw a TypeError that the
                    // catch in `run()` swallowed into a generic error page —
                    // half of ACPT-018. An admission belonging to nobody we
                    // selected is simply skipped.
                    const passengerAdmissions = admissions.filter(
                        (d: any) => (d?.passengerIds || []).includes(passengerId)
                    );

                    passengerAdmissions.forEach((passengerAdmission) => {
                        // FULFILLED only. This used to accept any admission whose
                        // id appeared in `booking.fulfillments`, and refunded
                        // entries are re-statused there rather than removed — so a
                        // shuttle already cancelled by a change-date went back into
                        // the batch, the backend rejected the whole request, and
                        // the booking was never cancelled. See
                        // isRefundableFulfillment.
                        if (!isRefundableFulfillment(passengerAdmission, fulfillmentIds)) return;
                        // Across directions as well as within this one.
                        if (alreadySent.has(passengerAdmission.id)) return;
                        if (blockedNol.has(passengerAdmission.id)) return;
                        if (!passengerFulfillmentIds.includes(passengerAdmission.id)) {
                            passengerFulfillmentIds.push(passengerAdmission.id);
                            alreadySent.add(passengerAdmission.id);
                        }
                    });
                });
            });

            if (passengerFulfillmentIds.length > 0) {
                outcome.sent = true;
                const response: any = await bookingReleaseOffers({ fulfillmentIds: passengerFulfillmentIds }, bookingId);
                if (response.code === 200) {
                    const BookingReleaseOffersResponse: IBookingReleaseOffersResponse = response.answer;
                    const allReleasedId = (BookingReleaseOffersResponse?.releaseOffers || []).map((d: IReleaseOffer) => d.id)

                    // A 200 that released nothing would otherwise skip the loop
                    // with failed=false and refund ids nothing has released. The
                    // refund rejects, so it is recoverable — but it costs a money
                    // call and reads as a refund bug rather than a release one.
                    if (allReleasedId.length === 0) {
                        outcome.failed = true;
                        outcome.reason = this.describeFailure(
                            "release-offers returned 200 but released nothing", response, passengerFulfillmentIds
                        );
                        outcome.detail = response?.answer;
                        return outcome;
                    }

                    // Add delay between each release offer patch
                    for (const releaseId of allReleasedId) {
                        const releasePatch: any = await bookingReleaseOffersPatch(releaseId, bookingId);
                        // A failed release PATCH is the worst state this path can
                        // reach — the admission is RELEASED but never refunded, so
                        // the passenger loses the travel right AND the money. It
                        // stops being FULFILLED either way, so isRefundableFulfillment
                        // excludes it and no retry from this screen recovers it.
                        // See shuttleIsReleasedNotRefunded. (Whether a RELEASED entry
                        // stays in `booking.fulfillments` is disputed between that
                        // file and refundableFulfillments.ts — nothing here depends
                        // on the answer, so it is deliberately not asserted.)
                        //
                        // STOP rather than refund on: the refund below is one batch
                        // over every id, so an unreleased id in it gets the whole
                        // request rejected — refunding nothing while costing a
                        // second money call and a muddier diagnosis.
                        // ponytail: leaves the already-released ids released-and-
                        // unrefunded, needing manual recovery. Refunding only the
                        // ids that DID release would need a releaseOfferId ->
                        // fulfillmentId map the response does not carry.
                        if (releasePatch?.code !== 200) {
                            outcome.failed = true;
                            outcome.reason = this.describeFailure(
                                `release-offers PATCH ${releaseId}`, releasePatch, passengerFulfillmentIds
                            );
                            outcome.detail = releasePatch;
                            break;
                        }
                        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                    }
                    if (outcome.failed) return outcome;

                    //Refund Offer
                    const refundOfferRequest: IRefundOfferRequest = {
                        fulfillmentIds: passengerFulfillmentIds,
                        overruleCode: null,
                        refundDate: new Date().toISOString()
                    }

                    await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                    const refundOfferResponse: any = await bookingRefundOffers(refundOfferRequest, bookingId);
                    if (refundOfferResponse.code == 200) {
                        //Refund offer with fulfillment
                        const refundOffer: IBookingRefundOffersResponse = refundOfferResponse.answer
                        const allRefundedId = (refundOffer?.refundOffers || []).map((refund: IRefundOffer) => refund.id)
                        // Symmetric with the releaseOffers guard above: a 200 that
                        // quoted nothing must not read as a completed refund.
                        if (allRefundedId.length === 0) {
                            outcome.failed = true;
                            outcome.reason = this.describeFailure(
                                "refund-offers returned 200 but quoted nothing",
                                refundOfferResponse, passengerFulfillmentIds
                            );
                            outcome.detail = refundOfferResponse?.answer;
                            return outcome;
                        }

                        // Add delay between each refund offer patch
                        for (const refundId of allRefundedId) {
                            const refundPatch: any = await bookingRefundOffersPatch(refundId, bookingId);
                            // No `break` here, unlike the release loop above. Each
                            // refund PATCH is independent — confirming the rest
                            // still returns that money — whereas an unreleased id
                            // poisons the single refund batch. Best-effort, then
                            // report the failure.
                            if (refundPatch?.code !== 200) {
                                outcome.failed = true;
                                outcome.reason = this.describeFailure(
                                    `refund-offers PATCH ${refundId}`, refundPatch, passengerFulfillmentIds
                                );
                                outcome.detail = refundPatch;
                            }
                            await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                        }

                        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                    } else {
                        outcome.failed = true;
                        outcome.reason = this.describeFailure(
                            "refund-offers", refundOfferResponse, passengerFulfillmentIds
                        );
                        outcome.detail = refundOfferResponse;
                    }
                } else {
                    outcome.failed = true;
                    outcome.reason = this.describeFailure(
                        "release-offers", response, passengerFulfillmentIds
                    );
                    outcome.detail = response;
                }

            }
        }

        return outcome;
    }

}

export const confirmCancelClickAction = new ConfirmCancelClickAction();